import { UseContractHookProps } from 'hooks/contracts/useWriteContractAndConfirm';
import { useEffect, useState } from 'react';
import {
  PROXY_CONTRACT_ADDRESS,
  ProxyContractABI,
} from 'services/contracts/Proxy/constants';
import { ContractFunction } from 'types/contracts/functions';
import { Address, encodeFunctionData } from 'viem';
import { Config, UseWriteContractReturnType, useWriteContract } from 'wagmi';

interface UseProxyParams extends UseContractHookProps {
  proxiedAddress: Address | undefined;
  callToAddress: Address | undefined;
  onSent?: (hash?: string) => void;
  onError?: (error: Error) => void;
}

type UseProxyReturnType = Omit<
  UseWriteContractReturnType<Config, unknown>,
  'writeContract'
> & {
  writeContract: VoidFunction;
};

export function useProxy({
  proxiedAddress,
  callToAddress,
  contractAbi,
  args,
  functionName,
  onSent,
  onError,
}: UseProxyParams): UseProxyReturnType {
  const [callData, setCallData] = useState('');

  const writeContractReturn = useWriteContract();
  const { writeContract, data, isPending, isError, error } =
    writeContractReturn;

  useEffect(() => {
    if (
      callToAddress &&
      !args?.some((arg) => arg === undefined || arg === null)
    ) {
      setCallData(
        encodeFunctionData<readonly object[], ContractFunction>({
          abi: contractAbi,
          functionName,
          args,
        }),
      );
    }
  }, [args, contractAbi, functionName, callToAddress]);

  const write = (): void =>
    writeContract(
      {
        abi: ProxyContractABI,
        address: PROXY_CONTRACT_ADDRESS,
        functionName: 'proxy',
        /// @dev Dispatch the given subcall (`callTo`, `callData`) from an account that the sender
        /// is authorised for through `addProxy`

        /// @param real The account that the proxy will make a call on behalf of
        /// @param callTo Recipient of the call to be made by the `real` account
        /// @param callData Data of the call to be made by the `real` account
        args: [
          proxiedAddress as Address,
          callToAddress as Address,
          callData as Address,
        ],
      },
      {
        onSuccess: onSent,
        onError: onError,
      },
    );

  return {
    ...writeContractReturn,
    data,
    writeContract: write,
    error,
    isError,
    isPending,
  };
}
